import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserMultiFormatOneDReader, IScannerControls } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat, Result } from '@zxing/library';
import css from './VinBarcodeScan.module.scss';
import { useLocalization } from '../../../../../context/Localization/localization';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import { CTAButton } from '../../../../../controls/ButtonBase/index';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../../../../context/Appointment/appointment';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { CameraDeviceCapabilities, VinBarcodeScanProps } from './VinBarcodeScan.types';
import { GoMotoComponent, GoMotoFunction } from '../../../../../API/GoMoto.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';
import { IAppointment } from '../../../../../API/GoMoto';

/**
 * This component handles creating the video stream to scan VIN Barcodes. The browser being used
 * will determine whether we use ZXing or Barcode Detection API for barcode scanning.
 *
 * @param onScan - {@link VinBarcodeScanProps.onScan onScan} description.
 * @param onCancel - {@link VinBarcodeScanProps.onCancel onCancel} description.
 */
export const VinBarcodeScan: GoMotoComponent<VinBarcodeScanProps> = memo(({
    onScan,
    onCancel,
}) => {
    const Localization: LocalizationType = useLocalization();
    const appointment: IAppointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const [loadingScanner, setLoadingScanner] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);

    // Android BarcodeDetection API
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const scannedVinRef = useRef<string | null>(null);
    const isBarcodeDetectorSupported: boolean = 'BarcodeDetector' in globalThis;

    // ZXing Decoder
    const controller = useRef<IScannerControls | undefined>(undefined);
    const barcodeReader = useMemo<BrowserMultiFormatOneDReader>(() => {
        return new BrowserMultiFormatOneDReader(
            new Map<DecodeHintType, unknown>([
                [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_39, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE]],
                [DecodeHintType.ASSUME_CODE_39_CHECK_DIGIT, true],
                [DecodeHintType.TRY_HARDER, true],
            ]),
            {
                delayBetweenScanAttempts: 500,
            });
    }, []);

    /**
     * This function iterates through all devices that are considered to be a videoinput device
     * and determines which of the devices are capable of controling a camera flash or the flashlight.
     * We then choose the first in the array.
     *
     * If for some reason no device is chosen, we will let the browser decide which camera to use when
     * created the video stream.
     */
    const findOptimalCameraDevice: GoMotoFunction<[void], Promise<string | null>> = async () => {
        try {
            const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();
            const videoDevices: MediaDeviceInfo[] = devices.filter((device: MediaDeviceInfo) => device.kind === 'videoinput');

            const streamPromises: Promise<CameraDeviceCapabilities | null>[] = videoDevices.map(async (device: MediaDeviceInfo) => {
                try {
                    const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId } });
                    const track: MediaStreamTrack = stream.getVideoTracks()[0];
                    const capabilities: MediaTrackCapabilities = track.getCapabilities();
                    track.stop();
                    return {
                        deviceId: device.deviceId,
                        hasTorch: 'torch' in capabilities && !!capabilities.torch,
                    };
                }
                catch {
                    return null;
                }
            });

            const results: (CameraDeviceCapabilities | null)[] = await Promise.all(streamPromises);

            const torchDevice: CameraDeviceCapabilities | undefined | null = results.find((result: CameraDeviceCapabilities | null) => result && result.hasTorch);

            if (torchDevice) {
                return torchDevice.deviceId;
            }

            return videoDevices[0].deviceId ? videoDevices[0].deviceId : null;
        }
        catch (error: unknown) {
            console.warn('Unabled to find torch-capable device: ', error);
            return null;
        }
    };

    /**
     * This function executes in the {@link startMediaStream} function and checks if the browser is a compatible android browser for the Barcode Detection API.
     * If the browser is compatible it returns the MediaStream with the desired constraints, otherwise it returns null to indicate it is not compatible.
     */
    const getDisplayMediaStream: GoMotoFunction<[MediaStreamConstraints], Promise<MediaStream | null>> = useCallback(async (constraints) => {
        if (isBarcodeDetectorSupported) {
            return navigator.mediaDevices.getUserMedia(constraints);
        }
        else {
            return null;
        }
    }, [isBarcodeDetectorSupported]);

    /**
     * On page load, we fire off this function to begin the process of finding the optimal camera device to use.
     * After either a device is selected or no optimal device is found, we create the MediaStream with the desired
     * constraints for the stream, and we update the MediaStream state. This will allow the barcode scanner to start
     * for the user.
     */
    const startMediaStream: GoMotoFunction<[void], Promise<void>> = useCallback(async () => {
        const selectedDeviceId: string | null = await findOptimalCameraDevice();

        const constraints: MediaStreamConstraints = {
            audio: false,
            video: {
                ...(selectedDeviceId !== null ? {
                    deviceId: {
                        exact: selectedDeviceId,
                    },
                } : undefined),
                facingMode: {
                    exact: 'environment',
                },
                width: {
                    min: 512,
                    exact: 2048,
                    ideal: 3024,
                    max: 5120,
                },
                height: {
                    min: 512,
                    exact: 2048,
                    ideal: 3024,
                    max: 5120,
                },
                frameRate: {
                    min: 24,
                    ideal: 30,
                    max: 60,
                },
                advanced: [
                    {
                        width: 4032,
                        height: 4032,
                    },
                    {
                        width: 5120,
                        height: 5120,
                    },
                ],
            },
        };

        /**
         * Sometimes the specific device ID request fails due to the device being theoretically accessible,
         * but not actually accessible. When this happens, it would normally error out and display a white screen.
         * To avoid this error from disrupting the user's ability to scan, we will fall back to this constraint and rerequest
         * the video device without specifying the exact camera to use.
         */
        const fallbackConstraint: MediaStreamConstraints = {
            audio: false,
            video: {
                facingMode: {
                    exact: 'environment',
                },
                width: {
                    min: 512,
                    exact: 2048,
                    ideal: 3024,
                    max: 5120,
                },
                height: {
                    min: 512,
                    exact: 2048,
                    ideal: 3024,
                    max: 5120,
                },
                frameRate: {
                    min: 24,
                    ideal: 30,
                    max: 60,
                },
                advanced: [
                    {
                        width: 4032,
                        height: 4032,
                    },
                    {
                        width: 5120,
                        height: 5120,
                    },
                ],
            },
        };

        /**
         * We default this to null so that in the event both the try and the catch fail, we
         * don't still try to begin a stream with a device that can't start a stream. This should
         * only occur if all video devices are not available for the browser to access and is a
         * user issue with the device.
         */
        let stream: MediaStream | null = null;

        try {
            stream = await getDisplayMediaStream(constraints);
        }
        catch {
            stream = await getDisplayMediaStream(fallbackConstraint);
        }

        if (stream !== null) {
            setMediaStream(stream);
        }
    }, [getDisplayMediaStream]);

    /**
     * Once we have scanned a valid VIN, stopped the stream and video player, and terminated all remaining attempts to scan a barcode,
     * we will update the appointment with the VIN and navigate back to the Vehicle screen.
     */
    const handleOnScan: GoMotoFunction<[void], Promise<void>> = useCallback(async () => {
        if (scannedVinRef.current !== null) {
            await setAppointment({
                ...appointment,
                make: '',
                chromeMake: '',
                model: '',
                chromeModel: '',
                year: '',
                vin: scannedVinRef.current,
            });
            onScan();
        }
        else {
            onScan();
        }
    }, [appointment, onScan, setAppointment]);

    useEffect(() => {
        if (isBarcodeDetectorSupported) {
            startMediaStream();
        }
    }, [isBarcodeDetectorSupported, startMediaStream]);

    useEffect(() => {
        const videoRefCurrent: HTMLVideoElement | null = videoRef.current;

        const stopVideoFeed = () => {
            if (videoRefCurrent) {
                videoRefCurrent.pause();
                videoRefCurrent.srcObject = null;
            }

            if (mediaStream) {
                mediaStream.getTracks()
                    .forEach((track) => track.stop());
            }
        };

        if (mediaStream !== null) {
            /**
             * This function uses recursion to maintain each attempted instance of scanning a barcode. If we fail to find a barcode,
             * we wait 1 second and then recurse. The 1 second delay helps with overall performance at a minor cost to the speed at which it
             * reads a barcode.
             *
             * Once we find a barcode we terminate reading any other scanned barcodes in the array of scanned barcodes, resolve, and then
             * chain resolves all the way back up the recusive tree to exit out of the barcode scanning process.
             *
             * @note
             * This is the best way I have been able to find that prevents the video stream from seemingly getting orphaned
             * and continuing in the background to try and scan a barcode. Using recursion lets us terminate all recursive instances
             * once we scan a VIN barcode and then proceed to disable the video stream without any lingering attempts to scan barcodes.
             */
            const tryReadingBarcode: GoMotoFunction<[BarcodeDetector], Promise<void>> = async (barcodeDetector) => {
                return new Promise<void>(async (resolve) => {
                    // If we cleaned up the video stream, stop the recursion and terminate
                    if (videoRefCurrent && !videoRefCurrent.paused) {
                        const barcodes: DetectedBarcode[] = await barcodeDetector.detect((videoRef.current as HTMLVideoElement));

                        if (!!barcodes && barcodes.length > 0) {
                            /**
                             * The camera can scan more than one barcode at the same time. We will use the
                             * first valid barcode that is a VIN for the VIN value scanned.
                             */
                            for (let i = 0; i < barcodes.length; i++) {
                                const { rawValue } = barcodes[i];

                                if (rawValue && rawValue.length === 17) {
                                    scannedVinRef.current = rawValue;
                                    break;
                                }
                            }

                            if (scannedVinRef.current !== null) {
                                resolve();
                            }
                            else {
                                await new Promise((resolve) => setTimeout(resolve, 1000));
                                await tryReadingBarcode(barcodeDetector);
                                resolve();
                            }
                        }
                        else {
                            if (scannedVinRef.current !== null) {
                                resolve();
                            }
                            else {
                                await new Promise((resolve) => setTimeout(resolve, 1000));
                                await tryReadingBarcode(barcodeDetector);
                                resolve();
                            }
                        }
                    }
                    else {
                        resolve();
                    }
                });
            };

            /**
             * This function occurs immediately after we play the video stream. We instantiate the Barcode Detection API and then
             * begin to try and scan barcodes. Once we finish scanning a barcode, we stop the video feed to clean up the remaining video stream
             * and then we proceed to navigate back to the Vehicle screen.
             *
             * If an error occurs, we log the error, stop the video stream, and set the error to display on screen.
             */
            const onPlay: GoMotoFunction<[void], Promise<void>> = async () => {
                try {
                    const barcodeDetector: BarcodeDetector = new BarcodeDetector();
                    await tryReadingBarcode(barcodeDetector);

                    stopVideoFeed();

                    await handleOnScan();
                }
                catch (error: unknown) {
                    console.error('Barcode Detection Error: ', error);
                    stopVideoFeed();
                    setError(error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' ? (
                        error.message
                    ) : (
                        typeof error === 'string' ?
                            error
                            : 'Barcode Detection Error'
                    ));
                }
            };

            /**
             * This function cleans up the video feed so that it does not continue to use the user's Camera device and run in the background.
             */
            if (mediaStream && videoRefCurrent) {
                videoRefCurrent.srcObject = mediaStream;

                videoRefCurrent.play()
                    .then(async () => {
                        setLoadingScanner(false);
                        await onPlay();
                    })
                    .catch((error: Error) => {
                        setLoadingScanner(false);
                        console.error(`Error playing video: ${error}`);
                        stopVideoFeed();
                        setError(error.message);
                    });
            }
        }

        return () => {
            stopVideoFeed();
        };
        /**
         * We don't want any dependency other than the mediaStream to affect this so that we only kick this off once.
         * If we add other dependencies to this array, we will attempt to open multiple video streams which will
         * sometimes orphan the video stream and run in the background, overheating older devices. This will also
         * occupy their Camera device without freeing it up which will prevent the user from using it until
         * they close the tab entirely.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaStream]);

    /**
     * This function uses ZXing to scan barcodes and is used by any browser that is incompatible with the Barcode Detection API.
     * Currently this includes any non-Android browser and also Firefox for Android.
     */
    const fallbackVideoRefCallback: GoMotoFunction<[HTMLVideoElement | null], Promise<void>> = async (ref) => {
        if (ref !== null && controller.current === undefined) {
            videoRef.current = ref;
            let selectedDeviceId = await findOptimalCameraDevice();

            const supports: MediaTrackSupportedConstraints = await navigator.mediaDevices.getSupportedConstraints();

            barcodeReader
                .decodeFromConstraints(
                    {
                        audio: false,
                        video: {
                            ...(selectedDeviceId ? {
                                deviceId: {
                                    exact: selectedDeviceId,
                                },
                            } : undefined),
                            facingMode: supports['facingMode'] ? {
                                exact: 'environment',
                            } : undefined,
                            width: supports['width'] ? {
                                min: 512,
                                exact: 2048,
                                ideal: 3024,
                                max: 5120,
                            } : undefined,
                            height: supports['height'] ? {
                                min: 512,
                                exact: 2048,
                                ideal: 3024,
                                max: 5120,
                            } : undefined,
                            aspectRatio: supports['aspectRatio'] ? {
                                min: 1,
                                exact: 1,
                                ideal: 1,
                                max: 1,
                            } : undefined,
                            frameRate: supports['frameRate'] ? {
                                min: 24,
                                ideal: 30,
                                max: 60,
                            } : undefined,
                        },
                    },
                    (videoRef.current as HTMLVideoElement),
                    async (result: Result | undefined) => {
                        const vinResult: string | undefined = result ? result.getText() : undefined;

                        if (vinResult && vinResult.length === 17) {
                            await setAppointment({
                                ...appointment,
                                make: '',
                                chromeMake: '',
                                model: '',
                                chromeModel: '',
                                year: '',
                                vin: vinResult,
                            });
                            onScan();
                        }
                    },
                )
                .then((scannerController) => {
                    controller.current = scannerController;
                    setLoadingScanner(false);
                })
                .catch((error: Error) => {
                    if (error.name === 'OverconstrainedError') {
                        barcodeReader
                            .decodeFromConstraints(
                                {
                                    audio: false,
                                    video: {
                                        ...(selectedDeviceId ? {
                                            deviceId: {
                                                exact: selectedDeviceId,
                                            },
                                        } : undefined),
                                        facingMode: supports['facingMode'] ? {
                                            ideal: 'environment',
                                        } : undefined,
                                        width: supports['width'] ? {
                                            min: 512,
                                            ideal: 3024,
                                            max: 5120,
                                        } : undefined,
                                        height: supports['height'] ? {
                                            min: 512,
                                            ideal: 3024,
                                            max: 5120,
                                        } : undefined,
                                        aspectRatio: supports['aspectRatio'] ? {
                                            min: 1,
                                            ideal: 1,
                                            max: 1,
                                        } : undefined,
                                        frameRate: supports['frameRate'] ? {
                                            min: 24,
                                            ideal: 30,
                                            max: 60,
                                        } : undefined,
                                    },
                                },
                                (videoRef.current as HTMLVideoElement),
                                async (result: Result | undefined) => {
                                    const vinResult: string | undefined = result ? result.getText() : undefined;

                                    if (vinResult && vinResult.length === 17) {
                                        await setAppointment({
                                            ...appointment,
                                            make: '',
                                            chromeMake: '',
                                            model: '',
                                            chromeModel: '',
                                            year: '',
                                            vin: vinResult,
                                        });
                                        onScan();
                                    }
                                },
                            )
                            .then((scannerController) => {
                                controller.current = scannerController;
                                setLoadingScanner(false);
                            })
                            .catch((retryError: Error) => {
                                console.error('Fallback Scanning Error: ', retryError);
                                setLoadingScanner(false);
                                setError(retryError.message);
                            });
                    }
                    else {
                        console.error('Fallback Scanning Error: ', error);
                        setLoadingScanner(false);
                        setError(error.message);
                    }
                });
        }
    };

    /**
     * This useEffect is only needed to clean up the ZXing barcode scanner.
     */
    useEffect(() => {
        return () => {
            if (controller.current) {
                controller.current.stop();
            }
        };
    }, []);

    return (
        <ScrollableBody
            proceedButtons={
                <CTAButton
                    styleType={'secondary'}
                    onClick={() => {
                        onCancel();
                    }}
                    label={Localization.Cancel}
                    id={Localization.Cancel}
                    ariaLabel={Localization.Cancel}
                />
            }
        >
            <h2 className={css.title}>{Localization.ScanVinBarcode}</h2>
            {
                error ? <div>{error}</div>
                    :
                    <section>
                        <div className={css.barcodeReaderContainer}>
                            <svg
                                viewBox={'0 0 100 100'}
                                className={css.barcodeReaderViewFinder}
                            >
                                <path
                                    fill={'none'}
                                    d={'M13,0 L0,0 L0,13'}
                                    stroke={'rgba(255, 0, 0, 0.5)'}
                                    strokeWidth={'5'}
                                />
                                <path
                                    fill={'none'}
                                    d={'M0,87 L0,100 L13,100'}
                                    stroke={'rgba(255, 0, 0, 0.5)'}
                                    strokeWidth={'5'}
                                />
                                <path
                                    fill={'none'}
                                    d={'M87,100 L100,100 L100,87'}
                                    stroke={'rgba(255, 0, 0, 0.5)'}
                                    strokeWidth={'5'}
                                />
                                <path
                                    fill={'none'}
                                    d={'M100,13 L100,0 87,0'}
                                    stroke={'rgba(255, 0, 0, 0.5)'}
                                    strokeWidth={'5'}
                                />
                            </svg>
                            <video
                                muted
                                ref={isBarcodeDetectorSupported ? videoRef : fallbackVideoRefCallback}
                                className={css.barcodeReaderVideo}
                            />
                            {loadingScanner ? (
                                <div className={css.spinner}>
                                    <Spinner />
                                </div>
                            ) : null}
                        </div>
                    </section>
            }
        </ScrollableBody>
    );
});